import React, { useCallback, useEffect, useState } from 'react'
import Wrapper from '../../components/Wrapper'
import { Helmet } from 'react-helmet'
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  TablePagination,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import qs from 'query-string'
import Cookies from 'js-cookie'
import { logRequest } from '../../store/actions/logs'
import { logs, menu, input, historyHeader, did } from '../../translate'

import LogDetail from '../../components/LogDetail'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PersonIcon from '@mui/icons-material/Person'
import ComputerIcon from '@mui/icons-material/Computer'
import LoginIcon from '@mui/icons-material/Login'
import InfoIcon from '@mui/icons-material/Info'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import IconButton from '@mui/material/IconButton'
import { activitiesStyles, greenTheme } from '../../helpers/style'

const { REACT_APP_SERVER } = process.env
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover

export default function Activities() {
  const [single, setSingle] = useState(0)
  const log = useSelector(state => state.logs.logsData)
  const logsStatus = useSelector(state => state.logs.logsStatus)
  const loading = logsStatus === 'request'
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const count = useSelector(state => state.logs.count)
  const page = useSelector(state => state.logs.page)
  const query = qs.parse(location.search, { arrayFormat: 'comma' })
  const langIndex = +localStorage.getItem('atsLang') || 1

  const [filters, setFilters] = useState({
    startTime: '',
    endTime: '',
    type: '',
    isAdmin: '',
    method: '',
  })

  useEffect(() => {
    const loadData = async () => {
      let yourDate = new Date()
      yourDate.setDate(yourDate.getDate() + 1)
      const fiveDaysAgo = new Date()
      fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5)

      const defaultStart = query.startTime || fiveDaysAgo.toISOString().split('T')[0]
      const defaultEnd = query.endTime || yourDate.toISOString().split('T')[0]

      setFilters({
        endTime: defaultEnd,
        startTime: defaultStart,
        type: query.type || '',
        isAdmin: query.isAdmin || '',
        method: query.method || '',
      })

      const limitNum = Number(query.limit)
      const limit =
        query.limit !== undefined && query.limit !== ''
          ? limitNum === -1
            ? 999999
            : (limitNum > 0 ? limitNum : 10)
          : 10
      const pageNum = query.page ? Number(query.page) : 1

      dispatch(
        logRequest(limit === -1 ? 999999 : limit, pageNum, {
          endTime: defaultEnd,
          startTime: defaultStart,
          type: query.type || '',
          isAdmin: query.isAdmin || '',
          method: query.method || '',
        })
      )
    }

    loadData()
  }, [])

  const handleLimit = useCallback(
    (newLimit) => {
      const limit = newLimit === -1 || newLimit === '-1' ? -1 : Number(newLimit) || 10
      const nextQuery = { ...query, limit, page: 1 }
      Cookies.set('logs', String(limit), { expires: 30 })
      navigate(
        `?${qs.stringify(nextQuery, {
          arrayFormat: 'comma',
          skipEmptyString: true,
        })}`
      )
      dispatch(
        logRequest(limit === -1 ? 999999 : limit, 1, {
          endTime: query.endTime || filters.endTime,
          startTime: query.startTime || filters.startTime,
          type: query.type || filters.type || '',
          isAdmin: query.isAdmin || filters.isAdmin || '',
          method: query.method || filters.method || '',
        })
      )
    },
    [location.search, dispatch, navigate, filters, query]
  )

  const handleChangePage = useCallback(
    (newPage) => {
      const pageNum = newPage + 1
      const nextQuery = { ...query, page: pageNum }
      navigate(
        `?${qs.stringify(nextQuery, {
          arrayFormat: 'comma',
          skipEmptyString: true,
        })}`
      )
      const limit = query.limit === -1 || query.limit === '-1' ? 999999 : (Number(query.limit) || 10)
      dispatch(
        logRequest(limit, pageNum, {
          endTime: query.endTime || filters.endTime,
          startTime: query.startTime || filters.startTime,
          type: query.type || filters.type || '',
          isAdmin: query.isAdmin || filters.isAdmin || '',
          method: query.method || filters.method || '',
        })
      )
    },
    [location.search, dispatch, navigate, filters, query]
  )

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }))
    query[name] = value
    navigate(
      `?${qs.stringify(query, {
        arrayFormat: 'comma',
        skipEmptyString: true,
      })}`
    )
  }

  const handleSubmit = useCallback(() => {
    dispatch(
      logRequest(Cookies.get('logs') || query.limit || 10, query.page || 1, filters)
    )
  }, [filters, dispatch, query.limit, query.page])

  const formatDate = date => {
    return new Date(date).toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const formatSource = link => {
    if (!link) return link
    
    const lowerLink = link.toLowerCase()
    
    // Check for specific patterns first
    if (lowerLink.includes('auto')) {
      return menu.autoCall1?.[langIndex] || 'Autocall / Group call'
    }
    
    // Check for sip provider (must check before general sip check)
    if (lowerLink.includes('sip') && (lowerLink.includes('provider') || lowerLink.includes('provide'))) {
      return menu.did?.[langIndex] || 'Main'
    }
    
    // Check for sip or included (employee)
    if (lowerLink.includes('sip') || lowerLink.includes('included')) {
      return menu.worker?.[langIndex] || 'Employee'
    }
    
    // Check for did (main number)
    if (lowerLink.includes('did')) {
      return menu.did?.[langIndex] || 'Main numbers'
    }

    if (lowerLink.includes('login')) {
      return logs.login?.[langIndex] || 'Login'
    }
    
    if (lowerLink.includes('ip')) {
      return logs.ip?.[langIndex] || 'IP list'
    }

    if (lowerLink.includes('confirm')) {
      return ''
    }

    if (lowerLink.includes('file')) {
      return menu.file?.[langIndex] || 'File'
    }

    if (lowerLink.includes('short')) {
      return menu.group?.[langIndex] || 'Short'
    }
    if (lowerLink.includes('ivr')) {
      return menu.ivr?.[langIndex] || 'IVR'
    }
    if (lowerLink.includes('forward')) {
      return menu.forward?.[langIndex] || 'Forward'
    }

    // Fallback to original formatting
    const parts = link.split('/')
    if (parts[0] && parts[0].includes('_')) {
      return parts[0].split('_').join(' ')
    } else if (!parts[0] && parts[1] && parts[1].includes('_')) {
      return parts[1].split('_').join(' ')
    }
    return link
  }

  const getMethodColor = method => {
    switch (method) {
      case 'POST':
        return 'success'
      case 'PUT':
        return 'warning'
      case 'DELETE':
        return 'error'
      case 'GET':
        return 'info'
      default:
        return 'default'
    }
  }

  return (
    <>
      <Helmet>
        <title>{menu.logs?.[langIndex] || 'Activities'}</title>
      </Helmet>
      <Wrapper>
        <div className="tableParent">
          <Paper sx={{ p: 3 }}>
            <Box sx={{ ...activitiesStyles.headerBox, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.3, }}>
              <Box sx={activitiesStyles.headerContentBox}>
                <IconButton
                  size={activitiesStyles.backButton.size}
                  sx={{ ...activitiesStyles.backButton, color, '&:hover': { backgroundColor: `${color}15` } }}
                  onClick={() => navigate(-1)}
                  aria-label="Back"
                >
                  <ArrowBackIosNewIcon sx={activitiesStyles.backIcon} />
                </IconButton>
                <Typography {...activitiesStyles.titleTypography}>
                  {menu.logs?.[langIndex] || 'Activities'}
                </Typography>
              </Box>
            </Box>


            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label={historyHeader.start?.[langIndex] || 'Start Date'}
                    type="date"
                    value={filters.startTime}
                    onChange={e => handleFilterChange('startTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label={historyHeader.end?.[langIndex] || 'End Date'}
                    type="date"
                    value={filters.endTime}
                    onChange={e => handleFilterChange('endTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{logs.changer?.[langIndex] || 'Changer'}</InputLabel>
                    <Select
                      value={filters.isAdmin}
                      label={logs.changer?.[langIndex] || 'Changer'}
                      onChange={e => handleFilterChange('isAdmin', e.target.value)}
                    >
                      <MenuItem value="">{input.select?.[langIndex] || 'All'}</MenuItem>
                      <MenuItem value="admin">{logs.admin?.[langIndex] || 'Admin'}</MenuItem>
                      <MenuItem value="user">{logs.user?.[langIndex] || 'User'}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{logs.method?.[langIndex] || 'Method'}</InputLabel>
                    <Select
                      value={filters.method}
                      label={logs.method?.[langIndex] || 'Method'}
                      onChange={e => handleFilterChange('method', e.target.value)}
                    >
                      <MenuItem value="">{input.select?.[langIndex] || 'All'}</MenuItem>
                      <MenuItem value="POST">{logs.methodPOST?.[langIndex] || 'POST'}</MenuItem>
                      <MenuItem value="PUT">{logs.methodPUT?.[langIndex] || 'PUT'}</MenuItem>
                      <MenuItem value="GET">{logs.methodGET?.[langIndex] || 'GET'}</MenuItem>
                      <MenuItem value="DELETE">
                        {logs.methodDELETE?.[langIndex] || 'DELETE'}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    sx={{ ...activitiesStyles.searchButton, backgroundColor: color, color: '#fff', '&:hover': { backgroundColor: hoverColor } }}
                  >
                    {input.search?.[langIndex] || 'Search'}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {loading ? (
              <Box sx={activitiesStyles.loadingBox}>
                <CircularProgress sx={{ ...activitiesStyles.loadingSpinner, color }} />
              </Box>
            ) : log?.length ? (
              <Box sx={activitiesStyles.activitiesContainer}>
                {log.map((activity, index) => (
                  <Card key={index} sx={{ ...activitiesStyles.activityCard, '&:hover': { boxShadow: `0 8px 24px ${color}26`, transform: 'translateY(-4px)', borderColor: color } }}>
                    <CardContent sx={activitiesStyles.cardContent}>
                      <Box sx={activitiesStyles.cardContentBox}>
                        <Avatar sx={{ ...activitiesStyles.getActivityAvatar(!!activity.admin), ...(activity.admin && { bgcolor: color, boxShadow: `0 4px 12px ${color}4D` }) }}>
                          {activity.admin ? 'A' : 'N'}
                        </Avatar>

                        <Box sx={activitiesStyles.activityContentBox}>
                          <Box sx={activitiesStyles.chipsContainer}>
                            <Chip
                              label={
                                activity.link?.includes('login')
                                  ? (logs.login?.[langIndex] || 'Login')
                                  : logs['method' + activity.method]?.[langIndex] || activity.method
                              }
                              color={getMethodColor(activity.method)}
                              size="small"
                              sx={activitiesStyles.methodChip}
                            />
                            {activity.admin && (
                              <Chip
                                icon={<PersonIcon sx={{ fontSize: '14px !important' }} />}
                                label={activity.admin || 'N'}
                                size="small"
                                variant="outlined"
                                sx={{ ...activitiesStyles.adminChip, borderColor: color, color }}
                              />
                            )}
                          </Box>

                          <Typography variant="h6" sx={activitiesStyles.activityTitle}>
                            {formatSource(activity.link)}
                          </Typography>

                          <Box sx={activitiesStyles.metadataBox}>
                            <Box sx={activitiesStyles.metadataItem}>
                              <AccessTimeIcon fontSize="small" sx={{ ...activitiesStyles.metadataIcon, color }} />
                              <Typography variant="caption" sx={activitiesStyles.metadataText}>
                                {formatDate(activity.date)}
                              </Typography>
                            </Box>

                            {activity.ip && (
                              <Box sx={activitiesStyles.metadataItem}>
                                <ComputerIcon fontSize="small" sx={{ ...activitiesStyles.metadataIcon, color }} />
                                <Typography variant="caption" sx={activitiesStyles.ipText}>
                                  {activity.ip}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {activity.link.includes('login') ? (
                          <Box sx={{ ...activitiesStyles.loginBox, backgroundColor: `${color}14`, border: `1px solid ${color}33` }}>
                            <LoginIcon sx={{ ...activitiesStyles.loginIcon, color }} />
                            <Typography variant="caption" sx={{ ...activitiesStyles.loginText, color }}>
                              {logs.login?.[langIndex] || 'Login'}
                            </Typography>
                          </Box>
                        ) : !activity.link.includes('confirm') ? (
                          <Box onClick={() => setSingle(activity.id)} sx={{ ...activitiesStyles.detailsBox, backgroundColor: color, '&:hover': { backgroundColor: hoverColor, transform: 'scale(1.05)', boxShadow: `0 4px 12px ${color}66` } }}>
                            <Typography variant="caption" sx={activitiesStyles.detailsText}>
                              {logs.detail?.[langIndex] || 'Details'}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={activitiesStyles.confirmedBox}>
                            <InfoIcon sx={activitiesStyles.confirmedIcon} />
                            <Typography variant="caption" sx={activitiesStyles.confirmedText}>
                              {logs.confirmed?.[langIndex] || 'Confirmed'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={activitiesStyles.emptyStateBox}>
                <Typography variant="body1" color="text.secondary">
                  {logs.no_found?.[langIndex] || 'No activities found'}
                </Typography>
              </Box>
            )}

            {log?.length > 0 && (
              <Box sx={activitiesStyles.paginationBox}>
                <TablePagination
                  labelRowsPerPage={input.rowsPerPage?.[langIndex] || 'Rows per page:'}
                  rowsPerPageOptions={[10, 25, 100, { label: did.all?.[langIndex] || 'All', value: -1 }]}
                  component="div"
                  count={count || 1}
                  rowsPerPage={query.limit || 10}
                  page={query.page - 1 || page - 1 || 0}
                  onPageChange={(event, newPage) => handleChangePage(newPage)}
                  onRowsPerPageChange={(e) => handleLimit(e.target.value)}
                />
              </Box>
            )}
          </Paper>
        </div>

        {single ? <LogDetail open={single} setOpen={setSingle} /> : null}
      </Wrapper>
    </>
  )
}
